import { isEmpty } from 'lodash';
import { FinancialStatementFullWrite, FinancialStatementPeriodFullWrite } from 'api';
import { FINANCIALS_REFERENCE_TYPE } from 'common/constants/documents';
import { dbShortDate } from 'utilities';
import getFinancialsData from './getFinancialsData';
import { FilesParams, FinancialPeriodColumn, FinancialTableData, MeasurementDateParams, NotesParams } from './types';
import updateOrCreateFinancialStatement from './updateOrCreateFinancialStatement';

/**
 *
 * @param {*} getFinancialsDataFn
 * @param {*} create
 * @param {*} getVersions
 * @param {*} update
 * @param {*} setFinancialStatementFn
 * @param {*} measurementDateParams
 * @param {*} filesParams
 * @param {*} notesParams
 */
const saveFinancialStatement = async (
  tableData: FinancialTableData,
  periods: FinancialPeriodColumn[],
  financialStatement: FinancialStatementFullWrite,
  create: (data: any) => Promise<unknown>,
  getVersions: () => Promise<FinancialStatementFullWrite[]>,
  update: (id: any, data: any) => Promise<unknown>,
  setFinancialStatementFn: (financialStatement: FinancialStatementFullWrite) => void,
  measurementDateParams: MeasurementDateParams,
  filesParams: FilesParams,
  notesParams: NotesParams = {} as NotesParams,
  showAdditionalHistoricalYears: boolean,
  useAdjustedEBITDA?: boolean,
  selectedDate?: string
) => {
  const { companyMeasurementDate, selectedMeasurementDate, measurementDates } = measurementDateParams;
  const { filesToSave, financialStatementId, addReferenceForExistingDocuments, setFilesToSave } = filesParams;
  const { notes, saveNotes, notesHasChanged, spreadsheetNoteHasChanged, spreadsheetNotes, saveSpreadsheetNotes } =
    notesParams;
  const { incomeStatement, balanceSheet, KPI } = tableData;

  // Save files in widget
  if (!isEmpty(filesToSave)) {
    addReferenceForExistingDocuments(
      selectedMeasurementDate.id!,
      filesToSave,
      financialStatementId!,
      FINANCIALS_REFERENCE_TYPE
    );
    setFilesToSave([]);
  }
  // Save files in widget
  if (!isEmpty(notes) && notesHasChanged) {
    saveNotes();
  }
  if (!isEmpty(spreadsheetNotes) && spreadsheetNoteHasChanged) {
    saveSpreadsheetNotes();
  }
  // make sure kpi data is sent too
  const financialStatementData = getFinancialsData(
    incomeStatement,
    balanceSheet,
    KPI,
    financialStatement,
    periods,
    selectedMeasurementDate
  );
  financialStatementData.company_measurement_date = companyMeasurementDate?.id;
  financialStatementData.measurement_date_id = selectedMeasurementDate?.id;
  financialStatementData.show_additional_historical_years = showAdditionalHistoricalYears;
  financialStatementData.use_adjusted_ebitda = useAdjustedEBITDA;
  financialStatementData.company_kpis = KPI.tableData.companyKPIs;
  financialStatementData.deleted_kpis = KPI.tableData.deletedKPIs || [];
  financialStatementData.sheet_attribute_mapping = financialStatement.sheet_attribute_mapping;
  const formattedSelectedDate = dbShortDate(selectedDate);
  financialStatementData.financials_date = formattedSelectedDate;

  if (!financialStatementData.name) {
    const md = measurementDates.find(item => item.id?.toString() === selectedMeasurementDate?.id?.toString());

    const measurementDate = md?.date;
    financialStatementData.name = `Financial Statement ${measurementDate}`;
  }
  financialStatementData.financial_statement_periods = financialStatementData.financial_statement_periods.map(
    (period: FinancialStatementPeriodFullWrite) => ({ ...period, financial_statement: financialStatement.id })
  );

  try {
    await updateOrCreateFinancialStatement({
      financialStatementData,
      getVersions,
      create,
      update,
      financialStatementSetter: setFinancialStatementFn,
    });
  } catch (error) {
    throw new Error(error as string);
  }
};

export default saveFinancialStatement;
